/**
 * Nysta — mobile menu drawer, defect fixes only.
 *
 * The drawer is Horizon's `<header-drawer>` (assets/header-drawer.js) rendering
 * snippets/header-drawer.liquid's Nysta markup, and its in-place `<details>`
 * accordion is deliberate and untouched. Nothing here adds to the interaction
 * model; it repairs three things that measured wrong at 360/390/430 in both
 * Chromium and WebKit.
 *
 * 1. Focus never entered the drawer on open. Horizon calls `trapFocus(details)`
 *    from inside `onAnimationEnd(drawer, ...)` and that callback does not fire
 *    here, so `document.activeElement` stayed on the hamburger for as long as
 *    the drawer was open — still true 2.5s after opening, in both engines.
 *    Focus now moves to the close control, the first thing in the drawer.
 *
 * 2. Once a submenu had been opened, Escape stopped closing the drawer.
 *    Horizon's handler resolves its target with `event.target.closest('details')`
 *    and focus is left on the submenu's own `<summary>`, so every later press
 *    kept addressing that already-closed submenu: measured drawerOpen still
 *    true after two Escapes, both engines. The two-step behaviour is kept —
 *    the first Escape closes the submenu, the second closes the drawer — by
 *    reading the submenu state in the capture phase, before Horizon's own
 *    handler has run and changed it.
 *
 * 3. Closing with the × left focus where it was instead of returning it to the
 *    hamburger. Escape already returned it correctly; the button did not.
 *
 * Scroll-lock is NOT touched: `<details scroll-lock>` plus assets/dialog.js
 * already holds the page at scrollY 0 behind the open drawer, verified in both
 * engines.
 */
(() => {
  const CONTAINER = 'Details-menu-drawer-container';
  const TRIGGER = 'summary.header__icon--menu';

  const container = () => document.getElementById(CONTAINER);
  const trigger = () => document.querySelector(TRIGGER);
  const isOpen = () => !!container()?.hasAttribute('open');

  /** The drawer's own close control, else the first focusable thing in it. */
  const firstFocusable = (root) =>
    root.querySelector('.nysta-mobile-nav__close') ||
    root.querySelector('a[href], button, summary, input, [tabindex]:not([tabindex="-1"])');

  /** Move focus into the drawer, and keep it there against WebKit. */
  const focusInto = () => {
    const el = container();
    const drawer = el && el.querySelector('.nysta-mobile-nav');
    const target = drawer && firstFocusable(drawer);
    if (!target) return;

    /* WebKit puts focus back on the <summary> after the drawer opens -- the
       browser's own focus-on-activate for a summary element, which lands after
       the toggle event and after any fixed delay short enough to be useful. So
       this does not guess a delay: it re-asserts until focus is inside the
       drawer, or until 1s has passed, whichever comes first. Chromium settles
       on the first attempt. */
    const deadline = Date.now() + 1000;
    const attempt = () => {
      if (!isOpen()) return;
      const d = container()?.querySelector('.nysta-mobile-nav');
      if (!d) return;
      if (d.contains(document.activeElement)) return;
      target.focus({ preventScroll: true });
      if (Date.now() < deadline) setTimeout(attempt, 100);
    };
    setTimeout(attempt, 60);
  };

  // ---------------------------------------------------------------- 1 and 3
  document.addEventListener(
    'toggle',
    (event) => {
      const el = event.target;
      if (!(el instanceof HTMLDetailsElement) || el.id !== CONTAINER) return;
      if (el.open) focusInto();
      else trigger()?.focus({ preventScroll: true });
    },
    { capture: true }
  );

  // ---------------------------------------------------------------------- 2
  // Capture runs before Horizon's own keyup listener on <header-drawer>, so
  // this is the submenu state as it was when the key went up.
  let submenuWasOpen = false;
  document.addEventListener(
    'keyup',
    (event) => {
      if (event.key !== 'Escape') return;
      const el = container();
      submenuWasOpen = !!(el && el.hasAttribute('open') && el.querySelector('details[open]'));
    },
    { capture: true }
  );

  // Bubble phase: Horizon has had its turn. If it closed the drawer, or if this
  // press belonged to a submenu, stand down.
  document.addEventListener('keyup', (event) => {
    if (event.key !== 'Escape') return;
    const el = container();
    if (!el || !el.hasAttribute('open')) return;
    if (submenuWasOpen) return;
    if (el.querySelector('details[open]')) return;

    const drawer = document.querySelector('header-drawer');
    if (drawer && typeof drawer.close === 'function') drawer.close();
    else el.removeAttribute('open');

    trigger()?.focus({ preventScroll: true });
  });
})();
